import { createSlice } from "@reduxjs/toolkit";
import { STATUS } from "./OrganizationSlice";
import { AccessibleOrganization } from "./OrganizationsV3Slice";
import {
  bulkDeactivateUsersAsync,
  bulkMoveUsersAsync,
  fetchAccessibleOrganizationsAsync,
  fetchUsersV3Async,
  fetchUsersV3StatsAsync,
} from "../thunks/UsersV3Thunk";

// Row shape of GET /master-data/v3/users. camelCase — do not mix with the v1
// snake_case user rows from UserSlice.
export interface UserV3 {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  organization: { id: number; name: string | null } | null;
  role: string;
  status: string | null; // ACTIVE | SIGNED_UP | INVITED | DEACTIVATED
  statusDate: string | null;
  lastLoginAt: string | null; // most recent successful login; null = never
  sessionCount: number; // successful logins; 0 when none
}

// GET /master-data/v3/users/stats — scoped to the caller, ignores table filters.
export interface UsersV3Stats {
  activeUsers: number;
  signedUpUsers: number;
  invitedUsers: number;
  deactivatedUsers: number;
}

export interface BulkSkippedUser {
  userId: string;
  // null for OUT_OF_SCOPE / NOT_FOUND: the caller may not see that user.
  email: string | null;
  reason: string;
}

// Responses of the two bulk endpoints; `skipped` lists users left untouched.
// `batchId` groups the audit rows the action wrote (null when nothing changed)
// and is shown as a support reference.
export interface BulkMoveResult {
  movedCount: number;
  skipped: BulkSkippedUser[];
  batchId: string | null;
}

export interface BulkDeactivateResult {
  deactivatedCount: number;
  skipped: BulkSkippedUser[];
  batchId: string | null;
}

interface UsersV3State {
  users: UserV3[];
  page: number;
  totalPages: number;
  totalItems: number;
  listStatus: string;
  listError: string | null;
  // requestId of the newest list fetch; older responses (fast sort/filter/page
  // clicks) are ignored so they cannot overwrite the current page.
  latestListRequestId: string | null;
  stats: UsersV3Stats | null;
  statsStatus: string;
  organizationOptions: AccessibleOrganization[];
  organizationOptionsStatus: string;
  bulkStatus: string;
  bulkError: string | null;
}

const initialState: UsersV3State = {
  users: [],
  page: 1,
  totalPages: 0,
  totalItems: 0,
  listStatus: STATUS.IDLE,
  listError: null,
  latestListRequestId: null,
  stats: null,
  statsStatus: STATUS.IDLE,
  organizationOptions: [],
  organizationOptionsStatus: STATUS.IDLE,
  bulkStatus: STATUS.IDLE,
  bulkError: null,
};

const usersV3Slice = createSlice({
  name: "usersV3",
  initialState,
  reducers: {
    resetUsersV3: () => initialState,
    // Called whenever a bulk dialog opens so a previous attempt's error does not
    // show for a new action.
    resetBulkState: (state) => {
      state.bulkStatus = STATUS.IDLE;
      state.bulkError = null;
    },
  },
  extraReducers: (builder) => {
    const bulkPending = (state: UsersV3State) => {
      state.bulkStatus = STATUS.LOADING;
      state.bulkError = null;
    };
    const bulkFulfilled = (state: UsersV3State) => {
      state.bulkStatus = STATUS.SUCCESS;
    };
    const bulkRejected = (state: UsersV3State, action: { payload?: unknown }) => {
      state.bulkStatus = STATUS.FAILED;
      state.bulkError = (action.payload as string) ?? "An unexpected error occurred";
    };

    builder
      .addCase(fetchUsersV3Async.pending, (state, action) => {
        state.listStatus = STATUS.LOADING;
        state.listError = null;
        state.latestListRequestId = action.meta.requestId;
      })
      .addCase(fetchUsersV3Async.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestListRequestId) return;
        state.listStatus = STATUS.SUCCESS;
        state.users = action.payload?.items ?? [];
        state.page = action.payload?.page ?? 1;
        state.totalPages = action.payload?.totalPages ?? 0;
        state.totalItems = action.payload?.totalItems ?? 0;
      })
      .addCase(fetchUsersV3Async.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestListRequestId) return;
        state.listStatus = STATUS.FAILED;
        state.listError = (action.payload as string) ?? "An unexpected error occurred";
        state.users = [];
      })

      .addCase(fetchUsersV3StatsAsync.pending, (state) => {
        state.statsStatus = STATUS.LOADING;
      })
      .addCase(fetchUsersV3StatsAsync.fulfilled, (state, action) => {
        state.statsStatus = STATUS.SUCCESS;
        state.stats = action.payload ?? null;
      })
      .addCase(fetchUsersV3StatsAsync.rejected, (state) => {
        // The cards fall back to "–"; the table still works.
        state.statsStatus = STATUS.FAILED;
        state.stats = null;
      })

      .addCase(fetchAccessibleOrganizationsAsync.pending, (state) => {
        state.organizationOptionsStatus = STATUS.LOADING;
      })
      .addCase(fetchAccessibleOrganizationsAsync.fulfilled, (state, action) => {
        state.organizationOptionsStatus = STATUS.SUCCESS;
        state.organizationOptions = action.payload?.items ?? [];
      })
      .addCase(fetchAccessibleOrganizationsAsync.rejected, (state) => {
        state.organizationOptionsStatus = STATUS.FAILED;
        state.organizationOptions = [];
      })

      .addCase(bulkMoveUsersAsync.pending, bulkPending)
      .addCase(bulkMoveUsersAsync.fulfilled, bulkFulfilled)
      .addCase(bulkMoveUsersAsync.rejected, bulkRejected)
      .addCase(bulkDeactivateUsersAsync.pending, bulkPending)
      .addCase(bulkDeactivateUsersAsync.fulfilled, bulkFulfilled)
      .addCase(bulkDeactivateUsersAsync.rejected, bulkRejected);
  },
});

export const { resetUsersV3, resetBulkState } = usersV3Slice.actions;
export default usersV3Slice.reducer;
